export const REFERENCE_SCENARIO = {
  version: 'darcy-colebrook-reference-v1',
  inputs: {
    diameterInches: 12,
    lengthMiles: 20,
    inletPsig: 600,
    outletPsig: 500,
    temperatureF: 60,
    specificGravity: 0.6,
    compressibility: 0.9,
    baseCompressibility: 1,
    roughnessMm: 0.045,
    viscosityPaS: 0.000011,
    atmosphericPsia: 14.7,
    basePressurePsia: 14.73,
    baseTemperatureF: 60,
    hhvBtuScf: 1037,
    heatRateBtuKwh: 8805,
  },
};

const NETL_NATURAL_GAS_SOURCE =
  'https://www.netl.doe.gov/projects/files/CostandPerformanceBaselineforFossilEnergyPlantsVol5NatGasElectricityGenUnitsFlexOperation_050523.pdf';
const NETL_COAL_AND_GAS_SOURCE =
  'https://netl.doe.gov/projects/files/CostandPerformanceBaselineFossilEnergyPlantsVolume1BituminousCoalNaturalGastoElectricity_052125.pdf';

export const GENERATION_CASES = [
  { id: 'SC1A', label: 'SC1A aeroderivative simple cycle — net HHV', heatRateBtuKwh: 8805, sourceUrl: NETL_NATURAL_GAS_SOURCE },
  { id: 'SC2A', label: 'SC2A aeroderivative simple cycle — net HHV', heatRateBtuKwh: 9467, sourceUrl: NETL_NATURAL_GAS_SOURCE },
  { id: 'R6A-S', label: 'R6A-S gas reciprocating engines — net HHV', heatRateBtuKwh: 8262, sourceUrl: NETL_NATURAL_GAS_SOURCE },
  { id: 'R12A-S', label: 'R12A-S gas reciprocating engines — net HHV', heatRateBtuKwh: 8319, sourceUrl: NETL_NATURAL_GAS_SOURCE },
  { id: 'B31A', label: 'B31A F-class combined cycle — net HHV', heatRateBtuKwh: 6385, sourceUrl: NETL_COAL_AND_GAS_SOURCE },
  { id: 'B32A.1', label: 'B32A.1 H-class 1×1 combined cycle — net HHV', heatRateBtuKwh: 6068, sourceUrl: NETL_COAL_AND_GAS_SOURCE },
  { id: 'B32A.2', label: 'B32A.2 H-class 2×1 combined cycle — net HHV', heatRateBtuKwh: 6113, sourceUrl: NETL_COAL_AND_GAS_SOURCE },
];

const INCH_TO_METRE = 0.0254;
const MILE_TO_METRE = 1609.344;
const PSI_TO_PASCAL = 6894.757293168;
const CUBIC_METRES_PER_CUBIC_FOOT = 0.028316846592;
const SECONDS_PER_DAY = 86400;
const UNIVERSAL_GAS_CONSTANT = 8.314462618;
const DRY_AIR_MOLAR_MASS_KG_MOL = 0.02896546;
const BTU_PER_KILOWATT_HOUR = 3412.141633;
const LAMINAR_REYNOLDS_LIMIT = 2300;
const TURBULENT_REYNOLDS_LIMIT = 4000;

const MODEL_WARNINGS = [
  'Reference-scenario model; not an engineering certification.',
  'Steady, isothermal, horizontal single-pipe model; excludes elevation, compressors, withdrawals, linepack, and network constraints.',
  'Results are conditional on the supplied inputs; they are not measured flow, available capacity, or headroom.',
];

function fahrenheitToKelvin(value) {
  return (value - 32) * 5 / 9 + 273.15;
}

function validateInputs(inputs) {
  const positiveInputs = [
    [inputs.diameterInches, 'Inside diameter'],
    [inputs.lengthMiles, 'Hydraulic length'],
    [inputs.specificGravity, 'Specific gravity'],
    [inputs.compressibility, 'Compressibility'],
    [inputs.baseCompressibility, 'Base compressibility'],
    [inputs.viscosityPaS, 'Dynamic viscosity'],
    [inputs.atmosphericPsia, 'Atmospheric pressure'],
    [inputs.basePressurePsia, 'Base pressure'],
    [inputs.hhvBtuScf, 'Higher heating value'],
    [inputs.heatRateBtuKwh, 'Heat rate'],
  ];
  for (const [value, name] of positiveInputs) {
    if (!Number.isFinite(value) || value <= 0) return `${name} must be a finite value greater than zero.`;
  }
  if (inputs.heatRateBtuKwh < BTU_PER_KILOWATT_HOUR) {
    return 'Heat rate below 3,412.141633 Btu/kWh would imply greater than 100% net HHV efficiency.';
  }
  if (!Number.isFinite(inputs.inletPsig)) return 'Inlet pressure must be a finite value.';
  if (!Number.isFinite(inputs.outletPsig)) return 'Outlet pressure must be a finite value.';
  if (!Number.isFinite(inputs.temperatureF) || fahrenheitToKelvin(inputs.temperatureF) <= 0) {
    return 'Gas temperature must be finite and above absolute zero.';
  }
  if (!Number.isFinite(inputs.baseTemperatureF) || fahrenheitToKelvin(inputs.baseTemperatureF) <= 0) {
    return 'Base temperature must be finite and above absolute zero.';
  }
  if (!Number.isFinite(inputs.roughnessMm) || inputs.roughnessMm < 0) {
    return 'Pipe roughness must be a finite value greater than or equal to zero.';
  }
  if (inputs.roughnessMm / 1000 >= inputs.diameterInches * INCH_TO_METRE) {
    return 'Pipe roughness must be smaller than the inside diameter.';
  }
  if (inputs.inletPsig + inputs.atmosphericPsia <= 0) {
    return 'Inlet absolute pressure must be greater than zero.';
  }
  if (inputs.outletPsig + inputs.atmosphericPsia <= 0) {
    return 'Outlet absolute pressure must be greater than zero.';
  }
  if (inputs.inletPsig < inputs.outletPsig) {
    return 'Inlet pressure must be greater than or equal to outlet pressure; swap the pressure boundaries for reverse flow.';
  }
  return null;
}

export function calculatePipelineScenario(inputs) {
  const validationError = validateInputs(inputs);
  if (validationError) return { ok: false, error: validationError };

  const diameterMetres = inputs.diameterInches * INCH_TO_METRE;
  const lengthMetres = inputs.lengthMiles * MILE_TO_METRE;
  const inletPascals = (inputs.inletPsig + inputs.atmosphericPsia) * PSI_TO_PASCAL;
  const outletPascals = (inputs.outletPsig + inputs.atmosphericPsia) * PSI_TO_PASCAL;
  const temperatureKelvin = fahrenheitToKelvin(inputs.temperatureF);
  const baseTemperatureKelvin = fahrenheitToKelvin(inputs.baseTemperatureF);
  const roughnessMetres = inputs.roughnessMm / 1000;
  const gasMolarMass = inputs.specificGravity * DRY_AIR_MOLAR_MASS_KG_MOL;
  const gasSpecificConstant = UNIVERSAL_GAS_CONSTANT / gasMolarMass;
  const area = Math.PI * diameterMetres ** 2 / 4;
  const pressureSquaredDifference = Math.abs(inletPascals ** 2 - outletPascals ** 2);
  const warnings = [...MODEL_WARNINGS];

  if (pressureSquaredDifference === 0) {
    return {
      ok: true,
      gasMmscfd: 0,
      gasDthDay: 0,
      netMw: 0,
      massFlowKgS: 0,
      reynoldsNumber: 0,
      darcyFrictionFactor: 0,
      warnings: [...warnings, 'Zero pressure drop produces zero calculated flow.'],
    };
  }
  const massFlowConstant = area * Math.sqrt(
    diameterMetres * pressureSquaredDifference
    / (inputs.compressibility * gasSpecificConstant * temperatureKelvin * lengthMetres),
  );
  const reynoldsFlowConstant = 4 * massFlowConstant
    / (Math.PI * diameterMetres * inputs.viscosityPaS);
  const relativeRoughness = roughnessMetres / diameterMetres;

  const laminarFrictionFactor = (64 / reynoldsFlowConstant) ** 2;
  const laminarReynoldsNumber = reynoldsFlowConstant / Math.sqrt(laminarFrictionFactor);
  let darcyFrictionFactor;
  let reynoldsNumber;
  if (laminarReynoldsNumber < LAMINAR_REYNOLDS_LIMIT) {
    darcyFrictionFactor = laminarFrictionFactor;
    reynoldsNumber = laminarReynoldsNumber;
    warnings.push('Laminar flow: Darcy friction factor uses f = 64/Re.');
  } else {
    const turbulentFrictionFactor = 1 / (-2 * Math.log10(
      relativeRoughness / 3.7 + 2.51 / reynoldsFlowConstant,
    )) ** 2;
    const turbulentReynoldsNumber = reynoldsFlowConstant / Math.sqrt(turbulentFrictionFactor);
    if (turbulentReynoldsNumber >= TURBULENT_REYNOLDS_LIMIT) {
      darcyFrictionFactor = turbulentFrictionFactor;
      reynoldsNumber = turbulentReynoldsNumber;
    } else {
      return { ok: false, error: 'Transitional flow needs a more detailed hydraulic model (Reynolds number 2,300–4,000).' };
    }
  }

  const massFlowKgS = massFlowConstant / Math.sqrt(darcyFrictionFactor);
  if (![massFlowKgS, reynoldsNumber, darcyFrictionFactor].every(Number.isFinite)) {
    return { ok: false, error: 'The hydraulic calculation did not produce a finite result for the supplied inputs.' };
  }

  const lowerPressurePascals = Math.min(inletPascals, outletPascals);
  const lowerPressureDensity = lowerPressurePascals
    / (inputs.compressibility * gasSpecificConstant * temperatureKelvin);
  const maximumVelocityMetresSecond = massFlowKgS / (lowerPressureDensity * area);
  const isothermalWaveSpeedMetresSecond = Math.sqrt(
    inputs.compressibility * gasSpecificConstant * temperatureKelvin,
  );
  const isothermalSpeedRatio = maximumVelocityMetresSecond / isothermalWaveSpeedMetresSecond;
  if (isothermalSpeedRatio >= 0.8) {
    return { ok: false, error: 'Calculated velocity is near the isothermal wave speed; this steady pressure-squared model is not applicable.' };
  }
  if (isothermalSpeedRatio >= 0.3) {
    warnings.push('Calculated peak velocity exceeds 0.3 times the isothermal wave speed; compressible acceleration effects may be material.');
  }

  const basePascals = inputs.basePressurePsia * PSI_TO_PASCAL;
  const baseDensityKgM3 = basePascals
    / (inputs.baseCompressibility * gasSpecificConstant * baseTemperatureKelvin);
  const baseCubicMetresSecond = massFlowKgS / baseDensityKgM3;
  const gasMmscfd = baseCubicMetresSecond * SECONDS_PER_DAY
    / CUBIC_METRES_PER_CUBIC_FOOT / 1_000_000;
  const gasDthDay = gasMmscfd * inputs.hhvBtuScf;
  const netMw = gasMmscfd * inputs.hhvBtuScf * 1000
    / (24 * inputs.heatRateBtuKwh);

  if (![gasMmscfd, gasDthDay, netMw].every(Number.isFinite)) {
    return { ok: false, error: 'The standard-volume or generation calculation did not produce a finite result.' };
  }
  return {
    ok: true,
    gasMmscfd,
    gasDthDay,
    netMw,
    massFlowKgS,
    reynoldsNumber,
    darcyFrictionFactor,
    warnings,
  };
}
